import { useId, useState, type FormEvent } from "react";

import {
  encryptionLabels,
  encryptionValues,
  smtpPorts,
  validateCredentialReference,
  validateCredentialSecret,
  validateHost,
  validateImapPort,
  validateMailboxEmail,
  validateName,
  validateSmtpPort,
  type Encryption,
  type MailboxDetail,
  type MailboxInput,
  type MailboxPatch,
} from "../entities/mailboxes";
import { TextField } from "./CounterpartyFields";
import { serverFieldErrors } from "./counterpartiesSupport";
import { MailboxNotice } from "./MailboxNotice";
import { Select } from "./Select";

/** Как задаётся доступ к ящику. Значение секрета наружу из Core не возвращается никогда. */
type CredentialMode = "keep" | "reference" | "password";

export type MailboxFormValues =
  | { mode: "create"; input: MailboxInput }
  | { mode: "edit"; patch: MailboxPatch };

// Core подставляет эти значения, когда поле не передано: форма показывает
// их сразу, чтобы человек видел, что именно будет сохранено.
const defaults = {
  imapHost: "imap.yandex.ru",
  imapPort: "993",
  smtpHost: "smtp.yandex.ru",
  smtpPort: "465",
  encryption: "ssl" as Encryption,
  folder: "INBOX",
};

function SelectField({
  label,
  value,
  onChange,
  options,
  error = null,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: readonly { value: string; label: string }[];
  error?: string | null;
}) {
  const id = useId();
  const errorId = `${id}-error`;
  return (
    <div className="field cp-field">
      <label htmlFor={id}>
        <span>{label}</span>
      </label>
      <Select
        id={id}
        value={value}
        aria-invalid={error === null ? undefined : true}
        aria-describedby={error === null ? undefined : errorId}
        onChange={(event) => onChange(event.target.value)}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </Select>
      {error === null ? null : (
        <small id={errorId} className="field-error" role="alert">
          {error}
        </small>
      )}
    </div>
  );
}

const encryptionOptions = encryptionValues.map((value) => ({
  value,
  label: encryptionLabels[value],
}));
const smtpPortOptions = smtpPorts.map((port) => ({
  value: String(port),
  label: String(port),
}));

type Field =
  | "name"
  | "email"
  | "username"
  | "imapHost"
  | "imapPort"
  | "smtpHost"
  | "smtpPort"
  | "folder"
  | "credentialRef"
  | "credentialSecret";

/**
 * Форма настроек ящика. Секрет вводится только сюда и только уходит на сервер:
 * его нет ни в ответе Core, ни в адресной строке, ни в браузерных хранилищах.
 * В карточке потом виден лишь признак, что ссылка на секрет задана.
 */
export function MailboxForm({
  mailbox,
  pending,
  error,
  onSubmit,
  onCancel,
  onChange,
  onRetry,
}: {
  /** Есть — редактирование, нет — создание. */
  mailbox?: MailboxDetail;
  pending: boolean;
  error: unknown;
  onSubmit: (values: MailboxFormValues) => void;
  onCancel: () => void;
  /** Человек изменил данные: прежний ключ идемпотентности больше не годится. */
  onChange?: () => void;
  onRetry?: () => void;
}) {
  const editing = mailbox !== undefined;
  const [name, setName] = useState(mailbox?.name ?? "");
  const [email, setEmail] = useState(mailbox?.email ?? "");
  const [username, setUsername] = useState(mailbox?.username ?? "");
  const [imapHost, setImapHost] = useState(
    mailbox?.imap.host ?? defaults.imapHost,
  );
  const [imapPort, setImapPort] = useState(
    String(mailbox?.imap.port ?? defaults.imapPort),
  );
  const [imapEncryption, setImapEncryption] = useState<string>(
    mailbox?.imap.encryption ?? defaults.encryption,
  );
  const [folder, setFolder] = useState(mailbox?.imap.folder ?? defaults.folder);
  const [smtpHost, setSmtpHost] = useState(
    mailbox?.smtp.host ?? defaults.smtpHost,
  );
  const [smtpPort, setSmtpPort] = useState(
    String(mailbox?.smtp.port ?? defaults.smtpPort),
  );
  const [smtpEncryption, setSmtpEncryption] = useState<string>(
    mailbox?.smtp.encryption ?? defaults.encryption,
  );
  const [enabled, setEnabled] = useState(mailbox?.enabled ?? true);
  const [mode, setMode] = useState<CredentialMode>(
    editing ? "keep" : "reference",
  );
  const [credentialRef, setCredentialRef] = useState("");
  const [credentialSecret, setCredentialSecret] = useState("");
  const [touched, setTouched] = useState<Partial<Record<Field, boolean>>>({});
  const [submitted, setSubmitted] = useState(false);

  const errors: Record<Field, string | null> = {
    name: validateName(name),
    email: validateMailboxEmail(email),
    username:
      username.trim().length > 320 ? "Логин длиннее 320 символов" : null,
    imapHost: validateHost(imapHost, true),
    imapPort: validateImapPort(imapPort),
    smtpHost: validateHost(smtpHost, true),
    smtpPort: validateSmtpPort(smtpPort),
    folder:
      folder.trim() === ""
        ? "Укажите папку"
        : folder.length > 255
          ? "Название папки длиннее 255 символов"
          : null,
    credentialRef:
      mode === "reference"
        ? (validateCredentialReference(credentialRef) ??
          (credentialRef.trim() === "" ? "Укажите ссылку на секрет" : null))
        : null,
    credentialSecret:
      mode === "password"
        ? (validateCredentialSecret(credentialSecret) ??
          (credentialSecret === "" ? "Введите пароль ящика" : null))
        : null,
  };
  const server = serverFieldErrors(error);
  const serverAliases: Record<Field, string | undefined> = {
    name: server.name,
    email: server.email,
    username: server.username,
    imapHost: server.imap_host,
    imapPort: server.imap_port,
    smtpHost: server.smtp_host,
    smtpPort: server.smtp_port,
    folder: server.folder,
    credentialRef: server.credential_ref,
    credentialSecret: server.credential_secret,
  };
  const shown = (field: Field): string | null =>
    submitted || touched[field] === true
      ? (errors[field] ?? serverAliases[field] ?? null)
      : (serverAliases[field] ?? null);
  const blur = (field: Field) => () =>
    setTouched((state) => ({ ...state, [field]: true }));

  function edit<T>(setter: (value: T) => void) {
    return (value: T) => {
      onChange?.();
      setter(value);
    };
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    setSubmitted(true);
    if (Object.values(errors).some((value) => value !== null)) return;

    const shared = {
      name: name.trim(),
      email: email.trim(),
      imap_host: imapHost.trim(),
      imap_port: Number(imapPort),
      imap_encryption: imapEncryption as Encryption,
      smtp_host: smtpHost.trim(),
      smtp_port: Number(smtpPort) as (typeof smtpPorts)[number],
      smtp_encryption: smtpEncryption as Encryption,
      folder: folder.trim(),
      // Пустой логин — это логин по умолчанию, как и при создании: адрес почты.
      username: username.trim() === "" ? email.trim() : username.trim(),
    };
    const credential =
      mode === "reference"
        ? { credential_ref: credentialRef.trim() }
        : mode === "password"
          ? { credential_secret: credentialSecret }
          : {};

    if (editing) {
      onSubmit({ mode: "edit", patch: { ...shared, ...credential } });
    } else {
      onSubmit({
        mode: "create",
        input: { ...shared, enabled, ...credential },
      });
    }
  }

  return (
    <form className="cp-form mb-form" onSubmit={submit} noValidate>
      <h2 className="cp-form__title">
        {editing ? "Настройки ящика" : "Новый почтовый ящик"}
      </h2>
      {error === null || error === undefined ? null : (
        <MailboxNotice
          error={error}
          fallback="Не удалось сохранить ящик."
          {...(onRetry === undefined ? {} : { onRetry })}
          retrying={pending}
        />
      )}

      <fieldset className="cp-fieldset mb-fieldset">
        <legend>Ящик</legend>
        <TextField
          label="Название"
          required
          maxLength={160}
          autoComplete="off"
          hint="Так ящик называется в списках, например «Общая почта КПО»"
          value={name}
          error={shown("name")}
          onBlur={blur("name")}
          onChange={edit(setName)}
        />
        <div className="cp-form__row">
          <TextField
            label="Адрес почты"
            required
            type="email"
            maxLength={320}
            autoComplete="off"
            value={email}
            error={shown("email")}
            onBlur={blur("email")}
            onChange={edit(setEmail)}
          />
          <TextField
            label="Логин"
            maxLength={320}
            autoComplete="off"
            hint="Если пусто — совпадает с адресом почты"
            value={username}
            error={shown("username")}
            onBlur={blur("username")}
            onChange={edit(setUsername)}
          />
        </div>
        {editing ? null : (
          <label className="cp-check">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(event) => edit(setEnabled)(event.target.checked)}
            />
            <span>Включить сразу — ящик начнёт синхронизироваться</span>
          </label>
        )}
      </fieldset>

      <fieldset className="cp-fieldset mb-fieldset">
        <legend>Входящая почта (IMAP)</legend>
        <div className="cp-form__row">
          <TextField
            label="Сервер"
            required
            maxLength={255}
            autoComplete="off"
            hint="Только серверы из списка, разрешённого политикой сервера"
            value={imapHost}
            error={shown("imapHost")}
            onBlur={blur("imapHost")}
            onChange={edit(setImapHost)}
          />
          <TextField
            label="Порт"
            required
            inputMode="numeric"
            maxLength={5}
            autoComplete="off"
            value={imapPort}
            error={shown("imapPort")}
            onBlur={blur("imapPort")}
            onChange={edit(setImapPort)}
          />
        </div>
        <div className="cp-form__row">
          <SelectField
            label="Шифрование"
            value={imapEncryption}
            options={encryptionOptions}
            onChange={edit(setImapEncryption)}
          />
          <TextField
            label="Папка"
            required
            maxLength={255}
            autoComplete="off"
            value={folder}
            error={shown("folder")}
            onBlur={blur("folder")}
            onChange={edit(setFolder)}
          />
        </div>
      </fieldset>

      <fieldset className="cp-fieldset mb-fieldset">
        <legend>Исходящая почта (SMTP)</legend>
        <div className="cp-form__row">
          <TextField
            label="Сервер"
            required
            maxLength={255}
            autoComplete="off"
            value={smtpHost}
            error={shown("smtpHost")}
            onBlur={blur("smtpHost")}
            onChange={edit(setSmtpHost)}
          />
          <SelectField
            label="Порт"
            value={smtpPort}
            options={smtpPortOptions}
            error={shown("smtpPort")}
            onChange={edit(setSmtpPort)}
          />
        </div>
        <SelectField
          label="Шифрование"
          value={smtpEncryption}
          options={encryptionOptions}
          onChange={edit(setSmtpEncryption)}
        />
      </fieldset>

      <fieldset className="cp-fieldset mb-fieldset">
        <legend>Доступ к ящику</legend>
        <div
          className="mb-choice"
          role="radiogroup"
          aria-label="Способ передать доступ"
        >
          {editing ? (
            <label>
              <input
                type="radio"
                name="credential-mode"
                checked={mode === "keep"}
                onChange={() => edit(setMode)("keep")}
              />
              <span>Оставить как есть</span>
            </label>
          ) : null}
          <label>
            <input
              type="radio"
              name="credential-mode"
              checked={mode === "reference"}
              onChange={() => edit(setMode)("reference")}
            />
            <span>Ссылка на секрет</span>
          </label>
          <label>
            <input
              type="radio"
              name="credential-mode"
              checked={mode === "password"}
              onChange={() => edit(setMode)("password")}
            />
            <span>Пароль ящика</span>
          </label>
        </div>
        {mode === "reference" ? (
          <TextField
            label="Ссылка на секрет"
            required
            maxLength={255}
            autoComplete="off"
            spellCheck={false}
            hint="Например env:MAILBOX_KPO_PASSWORD или docker-secret:mailbox_kpo. Сам пароль хранится на сервере."
            value={credentialRef}
            error={shown("credentialRef")}
            onBlur={blur("credentialRef")}
            onChange={edit(setCredentialRef)}
          />
        ) : null}
        {mode === "password" ? (
          <TextField
            label="Пароль ящика"
            required
            type="password"
            maxLength={255}
            autoComplete="new-password"
            spellCheck={false}
            hint="Пароль приложения почтового сервиса. Он отправляется на сервер и больше нигде не показывается."
            value={credentialSecret}
            error={shown("credentialSecret")}
            onBlur={blur("credentialSecret")}
            onChange={edit(setCredentialSecret)}
          />
        ) : null}
        {mode === "keep" ? (
          <p className="muted">
            Текущий доступ не меняется. Значение секрета Core не отдаёт, поэтому
            его нельзя посмотреть — только заменить.
          </p>
        ) : null}
      </fieldset>

      <div className="dw-form-actions">
        <button className="button button--primary" disabled={pending}>
          {pending ? "Сохраняем…" : editing ? "Сохранить" : "Создать ящик"}
        </button>
        <button
          className="button button--text"
          type="button"
          onClick={onCancel}
          disabled={pending}
        >
          Отмена
        </button>
      </div>
    </form>
  );
}
